/**
 * Grundkalender mit den Seitenzuständen: lädt als Platzhalterzeilen, Ladefehler mit Grund und
 * „Erneut laden“, veraltet nach misslungenem Nachladen (Raster bleibt, Hinweis darüber).
 */
import type { UseQueryResult } from '@tanstack/react-query';
import {
  problemText,
  useApiProblem,
  type AvailabilityDistributionCalendarResponse,
} from '@/shared/api';
import { SurfaceLoadError, SurfaceLoading, SurfaceStaleHint } from '@/shared/components';
import { AutomationProblemHint } from './automation/AutomationProblemHint';
import { GrundkalenderView } from './calendar';
import type { IsoRange } from './calendar/rangePickerModel';

interface Props {
  calendar: UseQueryResult<AvailabilityDistributionCalendarResponse>;
  canEdit: boolean;
  canFullSync: boolean;
  wholeFlatOf: ReadonlyMap<string, string>;
  onManageRules: () => void;
  onOpenMonth: (propertyId: string, ranges?: IsoRange[]) => void;
  onChanged: () => void;
}

export function AvailabilityCalendarCard({ calendar, ...props }: Props) {
  const { data, error } = calendar;
  const problem = useApiProblem(error);
  const retry = () => void calendar.refetch();
  if (!data) {
    return (
      <section
        aria-label="Grundkalender"
        className="bg-card border-hairline rounded-tile shadow-float overflow-hidden border"
      >
        {error ? (
          <SurfaceLoadError
            message={problemText(problem, 'Der Grundkalender konnte nicht geladen werden.')}
            onRetry={retry}
            retrying={calendar.isFetching}
          />
        ) : (
          <SurfaceLoading label="Grundkalender wird geladen" />
        )}
      </section>
    );
  }
  return (
    <GrundkalenderView
      data={data}
      {...props}
      loadingWindow={calendar.isPlaceholderData}
      notice={
        <>
          {error && <SurfaceStaleHint onRetry={retry} retrying={calendar.isFetching} />}
          <AutomationProblemHint />
        </>
      }
    />
  );
}
